/**
 * PaginationAdapter - Custom pagination component matching the original design
 * Maintains exact visual appearance and behavior of the legacy pagination
 */
import * as React from "react";
import { Table as TanStackTable } from "@tanstack/react-table";
import next from "../../../images/arrow-right.svg";
import prev from "../../../images/arrow-left.svg";
import arrowRight from "../../../images/chevron-right.svg";
import arrowLeft from "../../../images/chevron-left.svg";

interface PaginationAdapterProps {
  table: TanStackTable<any>;
  onPageChange?: (page: number) => void;
}

export const PaginationAdapter: React.FC<PaginationAdapterProps> = ({ 
  table, 
  onPageChange 
}) => {
  const [inputValue, setInputValue] = React.useState<number | string>(
    table.getState().pagination.pageIndex + 1
  );

  // Sync input value when page changes externally
  React.useEffect(() => {
    const currentPage = table.getState().pagination.pageIndex + 1;
    setInputValue(currentPage);
  }, [table.getState().pagination.pageIndex]);

  const handlePageChange = React.useCallback((page: number) => {
    const pageIndex = page - 1;
    table.setPageIndex(pageIndex);
    if (onPageChange) {
      onPageChange(pageIndex);
    }
  }, [table, onPageChange]);

  const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "") {
      setInputValue("");
      return;
    }
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0) {
      const totalPages = table.getPageCount();
      // Block values exceeding total pages
      if (numValue > totalPages) return;
      const clamped = Math.max(1, numValue);
      setInputValue(clamped);
      handlePageChange(clamped);
    }
  }, [table, handlePageChange]);

  const commitPageValue = React.useCallback(() => {
    // On blur, snap the displayed value to valid range
    const totalPages = table.getPageCount();
    let page = Number(inputValue);
    if (isNaN(page) || page <= 0) {
      page = 1;
    } else if (page > totalPages) {
      page = totalPages;
    }
    setInputValue(page);
    handlePageChange(page);
  }, [inputValue, table, handlePageChange]);

  const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["e", "E", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
      return;
    }
    // Block digit keys that would result in a value exceeding total pages
    if (e.key >= "0" && e.key <= "9") {
      const input = e.currentTarget;
      const currentVal = input.value === "" ? "" : input.value;
      const selectionStart = input.selectionStart ?? currentVal.length;
      const selectionEnd = input.selectionEnd ?? currentVal.length;
      const newVal = currentVal.slice(0, selectionStart) + e.key + currentVal.slice(selectionEnd);
      const numValue = Number(newVal);
      if (numValue > table.getPageCount()) {
        e.preventDefault();
        return;
      }
    }
    if (e.key === "Enter") {
      commitPageValue();
    }
  }, [commitPageValue, table]);

  const previousPage = React.useCallback(() => {
    const currentPage = table.getState().pagination.pageIndex + 1;
    const newPage = Math.max(1, currentPage - 1);
    setInputValue(newPage);
    handlePageChange(newPage);
  }, [table, handlePageChange]);

  const nextPage = React.useCallback(() => {
    const currentPage = table.getState().pagination.pageIndex + 1;
    const newPage = Math.min(table.getPageCount(), currentPage + 1);
    setInputValue(newPage);
    handlePageChange(newPage);
  }, [table, handlePageChange]);

  const goToPage = React.useCallback((pageIndex: number) => {
    const page = pageIndex + 1;
    setInputValue(page);
    handlePageChange(page);
  }, [handlePageChange]);

  const currentPage = typeof inputValue === "number" ? inputValue : 0;
  const totalPages = table.getPageCount();

  return (
    <div className="-pagination">
      <div className="-previous">
        <button
          className="-btn"
          disabled={currentPage <= 1}
          onClick={() => goToPage(0)}
        >
          <img src={arrowLeft} className="rotateLeft" alt="Previous Icon" />First
        </button>
      </div>
      <div className="-previous">
        <button
          disabled={!table.getCanPreviousPage()}
          className="-btn"
          onClick={previousPage}
        >
          <img src={prev} alt="Previous Icon"/>Previous
        </button>
      </div>
      
      <div className="-center">
        <span className="-pageInfo">
          Page{" "}
          <div className="-pageJump">
            <input
              aria-label="jump to page"
              type="number"
              value={totalPages > 0 ? inputValue : 0}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              onBlur={commitPageValue}
              min={totalPages === 0 ? 0 : 1}
              max={totalPages}
              disabled={totalPages === 0}
            />
          </div>{" "}
          of <span className="-totalPages">{totalPages || 1}</span>
        </span>
      </div>
      
      <div className="-next">
        <button
          disabled={!table.getCanNextPage()}
          className="-btn"
          onClick={nextPage}
        >
          Next<img src={next} alt="Next Icon" />
        </button>
      </div>
      <div className="-next">
        <button
          disabled={currentPage >= totalPages || totalPages === 0}
          className="-btn"
          onClick={() => goToPage(totalPages - 1)}
        >
          Last<img className="rotateRight" src={arrowRight} alt="Next Icon" />
        </button>
      </div>
    </div>
  );
};

/**
 * Legacy Pagination component for backward compatibility
 * This matches the exact implementation from the original Table.tsx
 */
export class LegacyPaginationComponent extends React.Component<any, any> {
  previousValue = 0;
  
  constructor(props: any) {
    super(props);

    this.state = {
      inputValue: this.props.page + 1
    };
    this.previousValue = this.props.page + 1;
  }

  static getDerivedStateFromProps(nextProps: any, prevState: any) {
    const { page } = nextProps;
    const { inputValue } = prevState;

    if (page + 1 !== inputValue) {
      return {
        inputValue: page + 1
      };
    }
    return null;
  }

  changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({
      inputValue: e.target.value
    });
  };

  blurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = Number(e.target.value);
    const { pages } = this.props;

    if (inputValue > pages) {
      this.setState({ inputValue: pages });
      this.props.onPageChange(pages - 1);
    } else if (inputValue <= 0) {
      this.setState({ inputValue: 1 });
      this.props.onPageChange(0);
    } else {
      this.setState({ inputValue });
      this.props.onPageChange(inputValue - 1);
    }
  };

  keyPressHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const inputValue = Number((e.target as HTMLInputElement).value);
      const { pages } = this.props;

      if (inputValue > pages) {
        this.setState({ inputValue: pages });
        this.props.onPageChange(pages - 1);
      } else if (inputValue <= 0) {
        this.setState({ inputValue: 1 });
        this.props.onPageChange(0);
      } else {
        this.setState({ inputValue });
        this.props.onPageChange(inputValue - 1);
      }
    }
  };

  render() {
    const {
      pages,
      page,
      canPrevious,
      canNext,
      className,
      PreviousComponent = () => <button type="button">Previous</button>,
      NextComponent = () => <button type="button">Next</button>
    } = this.props;

    return (
      <div className={`rt-pagination ${className || ""}`}>
        <div className="-previous">
          <button
            type="button"
            onClick={() => this.props.onPageChange(0)}
            disabled={!canPrevious}
            className="-btn"
          >
            <img src={prev} alt="First" />
          </button>
          <PreviousComponent
            onClick={() => this.props.onPageChange(page - 1)}
            disabled={!canPrevious}
            className="-btn"
          >
            <img src={arrowLeft} alt="Previous" />
          </PreviousComponent>
        </div>
        <div className="-center">
          <span className="-pageInfo">
            Page{" "}
            <div className="-pageJump">
              <input
                type="number"
                onChange={this.changeHandler}
                value={this.state.inputValue}
                onBlur={this.blurHandler}
                onKeyPress={this.keyPressHandler}
              />
            </div>{" "}
            of <span className="-totalPages">{pages || 1}</span>
          </span>
        </div>
        <div className="-next">
          <NextComponent
            onClick={() => this.props.onPageChange(page + 1)}
            disabled={!canNext}
            className="-btn"
          >
            <img src={arrowRight} alt="Next" />
          </NextComponent>
          <button
            type="button"
            onClick={() => this.props.onPageChange(pages - 1)}
            disabled={!canNext}
            className="-btn"
          >
            <img src={next} alt="Last" />
          </button>
        </div>
      </div>
    );
  }
}